import { ChevronRight } from 'lucide-react';
import { iconForCategory } from '@/lib/categoryIcons';
import FlagBadge from '@/components/FlagBadge';
import { recipeCount } from '@/lib/format';
import { SECTION_SIZE } from '@/lib/sections';
import RecipeMosaic from '@/components/RecipeMosaic';
import type { Category, RecipeSummary } from '@shared/schemas';

interface Props {
  category: Category;
  /** up to DESKTOP_SECTION_SIZE recipes (picked day-stable by the parent) —
   * RecipeMosaic's feedCap trims that back down to SECTION_SIZE on phone. */
  recipes: RecipeSummary[];
  /** how many recipes the category holds in total, for the header */
  total: number;
  categoriesById: Map<number, Category>;
  isFavourite: (id: number) => boolean;
  onOpenCategory: (slug: string) => void;
}

// One category block below the home feed: header (icon, name, count, "Vše →")
// over a hairline rule, then the same staggered card block the feed opens with.
export default function CategorySection({
  category,
  recipes,
  total,
  categoriesById,
  isFavourite,
  onOpenCategory,
}: Props) {
  const Icon = iconForCategory(category);

  return (
    <section className="pt-8">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="relative h-8 w-8 shrink-0">
            <span className="flex h-full w-full items-center justify-center rounded-full bg-accent-soft text-text">
              <Icon size={17} />
            </span>
            <FlagBadge flag={category.flag} size={13} ring={1.5} />
          </span>
          <div className="min-w-0">
            <h2 className="truncate font-serif text-lg font-extrabold text-text">
              {category.name}
            </h2>
            <p className="text-[11px] tabular-nums text-text-muted">{recipeCount(total)}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpenCategory(category.slug)}
          className="inline-flex shrink-0 items-center gap-0.5 text-xs font-bold text-text-muted transition-colors hover:text-text"
        >
          Vše <ChevronRight size={13} />
        </button>
      </div>
      <div className="mt-1 h-px bg-border" />

      <RecipeMosaic
        recipes={recipes}
        categoriesById={categoriesById}
        isFavourite={isFavourite}
        className="pt-3"
        feedCap={SECTION_SIZE}
      />
    </section>
  );
}
